/**
 * Generic credential shapes the product recognizes, for any boundary that must not carry one.
 *
 * Two consumers today: the Getting Started free-text field, which blocks a paste before a
 * debounced PATCH can earn an unexplainable 422, and AI Preview's Support AI forwarding, which
 * redacts before copying a message into Quickchat's own support tenant. One list, so a shape
 * added for either protects both.
 *
 * Mirrors `appquickchat/api/getting_started_progress/other_text_guard.py` pattern for pattern;
 * `other_text_guard_corpus.json` is asserted against both implementations so a pattern added to
 * one and not the other fails a test instead of shipping that mismatch.
 */
const SECRET_PATTERNS: RegExp[] = [
  /sk-(?:ant-|proj-|or-v1-)?[A-Za-z0-9_-]{20,}/,
  /\b(?:sk|rk)_(?:live|test)_[A-Za-z0-9]{8,}\b/,
  /gsk_[A-Za-z0-9]{20,}/,
  /AIzaSy[A-Za-z0-9_-]{10,}/,
  /AQ\.[A-Za-z0-9_-]{20,}/,
  /\bghp_[A-Za-z0-9]{20,}/,
  /\bAKIA[A-Z0-9]{16}\b/,
  /\bxox[baprs]-[A-Za-z0-9-]{12,}\b/,
  /\bBearer\s+[A-Za-z0-9._~+/=-]{12,}/i,
  /mongodb(?:\+srv)?:\/\//i,
  /:\/\/[^/@\s:]+:[^/@\s]+@/,
  /\b\d{8,}:AA[\w-]{20,}/,
  /\b[\w-]{24,26}\.[\w-]{6}\.[\w-]{25,}\b/,
  /\b[A-Za-z0-9_-]{40,}\b/,
];

// Discord OAuth2 secrets are ~32 URL-safe characters; keep this contextual to avoid generic long-word false positives.
const DISCORD_CLIENT_SECRET_PATTERN = /^[A-Za-z0-9_-]{25,50}$/;

// No overall length floor, because the API applies none either. A 20-character floor is exactly how
// `sk_live_` plus eight characters — a 16-character live key — used to pass here and get a 422.
export const looksLikeSecret = (text: string): boolean =>
  SECRET_PATTERNS.some((pattern) => pattern.test(text.trim()));

export const looksLikeDiscordClientSecret = (text: string): boolean =>
  DISCORD_CLIENT_SECRET_PATTERN.test(text.trim());
